import React, { useEffect, useState, useCallback } from 'react';
import { MdClose, MdInsights, MdChevronRight, MdChevronLeft, MdOpenInNew, MdReceiptLong } from 'react-icons/md';
import { createBillingPortalSession } from '../api';
import {
  approveUsageBillingConsent,
  fetchCompanyCreditBilling,
  fetchCompanyUsageBilling,
  fetchMemberUsageConversations,
  revokeUsageBillingConsent,
  setTopupEmailPreference,
  setUsageAlertPreference,
  setUsageOverageCap,
  updateUsageBillingMemberRole,
} from './atlas/atlasApi';
import {
  CreditBreakdown,
  CreditPeriodHeader,
  CreditSummary,
  creditChartData,
  creditFootnote,
  fmtCredits,
} from './UsageCredits';
import './UsageBillingModal.css';

const fmtUsd = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MEMBER_COLORS = ['#2563eb', '#e26313', '#7c3aed', '#059669', '#db2777', '#0891b2', '#ca8a04', '#475569'];

const fmtCycleDate = (value) => {
  if (!value) return '—';
  return new Date(`${value}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
};

function daysInPeriod(start, end) {
  if (!start || !end) return [];
  const days = [];
  const cursor = new Date(`${start}T00:00:00Z`);
  const stop = new Date(`${end}T00:00:00Z`);
  while (cursor < stop && days.length < 32) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

const amountOf = (entry) => Number(entry.amount ?? entry.priced_usd ?? 0);
const TOPUP_EMAIL_OPTIONS = [100, 200, 500, 1000];
const fmtWholeUsd = (v) => `$${Number(v || 0).toLocaleString()}`;
const fmtCreditTotal = (value) => `${fmtCredits(value)} credits`;

function UsageDailyChart({ data, personalOnly = false, format = fmtUsd, title = null }) {
  const members = data?.members || [];
  const [selectedIds, setSelectedIds] = useState([]);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const allIds = members.map((member) => Number(member.user_id));
  const selectionKey = allIds.join(',');

  useEffect(() => {
    setSelectedIds(allIds);
    // selectionKey intentionally resets the filter when the loaded team changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  const colors = Object.fromEntries(allIds.map((id, index) => [id, MEMBER_COLORS[index % MEMBER_COLORS.length]]));
  const membersById = Object.fromEntries(members.map((member) => [Number(member.user_id), member]));
  const byDate = Object.fromEntries((data?.daily_by_member || []).map((row) => [row.date, row.members || []]));
  const days = daysInPeriod(data?.period?.start, data?.period?.end);
  const totals = days.map((day) => (byDate[day] || [])
    .filter((entry) => selectedIds.includes(Number(entry.user_id)))
    .reduce((sum, entry) => sum + amountOf(entry), 0));
  const max = Math.max(...totals, 0.01);

  const toggle = (userId) => {
    setSelectedIds((current) => (
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId]
    ));
  };

  return (
    <div className="usage-billing-chart-card">
      <div className="usage-billing-chart-heading">
        <div>
          <strong>{title || (data?.period?.is_current ? 'Current billing cycle by day' : 'Billing cycle by day')}</strong>
          <span>{personalOnly ? 'Your daily Atlas usage' : 'Stacked by team member'} · {data?.period?.label}</span>
        </div>
        <span className="usage-billing-chart-total">{format(totals.reduce((sum, value) => sum + value, 0))}</span>
      </div>
      {!personalOnly && <div className="usage-billing-member-filters" aria-label="Filter usage chart by team member">
        {members.map((member) => {
          const id = Number(member.user_id);
          const active = selectedIds.includes(id);
          return (
            <button type="button" key={id} className={active ? 'active' : ''} onClick={() => toggle(id)}>
              <span style={{ background: colors[id] }} />
              {member.name || member.email || `User ${id}`}
            </button>
          );
        })}
      </div>}
      <div className="usage-billing-chart" role="group" aria-label={personalOnly ? 'Your daily usage bar chart' : 'Daily team usage stacked bar chart'}>
        {days.map((day, dayIndex) => {
          const entries = (byDate[day] || []).filter((entry) => selectedIds.includes(Number(entry.user_id)));
          const total = totals[dayIndex] || 0;
          const showTooltip = hoveredSegment?.day === day;
          return (
            <div className="usage-billing-chart-day" key={day}>
              <div className="usage-billing-chart-bar">
                {entries.map((entry) => {
                  const userId = Number(entry.user_id);
                  const member = membersById[userId];
                  const amount = amountOf(entry);
                  const isHovered = hoveredSegment?.userId === userId;
                  return (
                    <button
                      type="button"
                      key={entry.user_id}
                      className={`usage-billing-chart-segment${isHovered ? ' is-hovered' : ''}${hoveredSegment && !isHovered ? ' is-dimmed' : ''}`}
                      aria-label={`${member?.name || member?.email || `User ${userId}`}, ${day}, ${format(amount)}`}
                      onMouseEnter={() => setHoveredSegment({ day, userId })}
                      onMouseLeave={() => setHoveredSegment(null)}
                      onFocus={() => setHoveredSegment({ day, userId })}
                      onBlur={() => setHoveredSegment(null)}
                      style={{
                        height: `${Math.max((amount / max) * 100, amount > 0 ? 2 : 0)}%`,
                        background: colors[userId],
                      }}
                    />
                  );
                })}
              </div>
              {showTooltip && (
                <div className={`usage-billing-chart-tooltip${dayIndex < 3 ? ' align-left' : ''}${dayIndex > days.length - 4 ? ' align-right' : ''}`}>
                  <strong>{new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}</strong>
                  <span className="usage-billing-chart-tooltip-total">{personalOnly ? 'Daily total' : 'Team total'} {format(total)}</span>
                  {entries.map((entry) => {
                    const userId = Number(entry.user_id);
                    const member = membersById[userId];
                    return (
                      <span key={userId} className={hoveredSegment.userId === userId ? 'active' : ''}>
                        <i style={{ background: colors[userId] }} />
                        <em>{member?.name || member?.email || `User ${userId}`}</em>
                        <b>{format(amountOf(entry))}</b>
                      </span>
                    );
                  })}
                </div>
              )}
              <span>{dayIndex % 5 === 0 || dayIndex === days.length - 1 ? new Date(`${day}T00:00:00Z`).getUTCDate() : ''}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PORTAL_ERRORS = [
  [/does not have a Stripe customer/i, 'Your company doesn\'t have a billing account yet. It appears after your first subscription payment.'],
  [/owners and admins/i, 'Only company owners and admins can open billing.'],
];

// Invoices, receipts and the card on file live in Stripe's billing portal. The
// backend creates a one-time session for the company's Stripe customer and we
// send the browser there; Stripe returns it to this page afterwards.
function BillingPortalCard({ companyId, canOpen }) {
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');

  const open = async () => {
    setOpening(true);
    setError('');
    try {
      const { url } = await createBillingPortalSession({ returnUrl: window.location.href, companyId });
      if (!url) throw new Error('no url');
      window.location.assign(url);
    } catch (e) {
      const message = e?.response?.data?.error || '';
      const known = PORTAL_ERRORS.find(([pattern]) => pattern.test(message));
      setError(known ? known[1] : 'Billing couldn\'t be opened. Please try again.');
      setOpening(false);
    }
  };

  return (
    <div className="usage-billing-portal-card">
      <MdReceiptLong className="usage-billing-portal-icon" size={20} />
      <div className="usage-billing-portal-text">
        <strong>Invoices &amp; payment</strong>
        <span>
          {canOpen
            ? 'See past invoices, download receipts and update the card on file in Stripe.'
            : 'Invoices and payment details are managed by your company\'s owners and admins.'}
        </span>
        {error && <span className="usage-billing-portal-error" role="alert">{error}</span>}
      </div>
      {canOpen && (
        <button type="button" onClick={open} disabled={opening}>
          {opening ? 'Opening…' : 'Open billing'}
          {!opening && <MdOpenInNew size={14} />}
        </button>
      )}
    </div>
  );
}

const UsageBillingModal = ({ isOpen = true, onClose, onOpenConversation, embedded = false, ownerUserId = null }) => {
  const [data, setData] = useState(null);
  const [creditData, setCreditData] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // member drill-down
  const [member, setMember] = useState(null); // {user_id, name}
  const [convs, setConvs] = useState([]);
  const [convOffset, setConvOffset] = useState(0);
  const [convTotal, setConvTotal] = useState(0);
  const [convHasMore, setConvHasMore] = useState(false);
  const [convLoading, setConvLoading] = useState(false);

  // cap editing
  const [periodOffset, setPeriodOffset] = useState(0);
  const [capInput, setCapInput] = useState('');
  const [capSaving, setCapSaving] = useState(false);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertPctInput, setAlertPctInput] = useState('80');
  const [alertSaving, setAlertSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [topupEnabled, setTopupEnabled] = useState(false);
  const [topupEveryUsd, setTopupEveryUsd] = useState(100);
  const [topupSaving, setTopupSaving] = useState(false);
  const [topupMessage, setTopupMessage] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState(null);
  const [roleSaving, setRoleSaving] = useState(null);
  const [roleError, setRoleError] = useState('');
  const PAGE = 10;

  const loadSummary = useCallback((offset = 0) => {
    setLoading(true);
    setError(null);
    fetchCompanyUsageBilling(offset, ownerUserId)
      .then((d) => {
        setData(d);
        setCapInput(d?.overage_cap_usd != null ? String(d.overage_cap_usd) : '');
        setAlertEnabled(Boolean(d?.alert_preference?.enabled));
        setAlertPctInput(String(d?.alert_preference?.threshold_pct ?? 80));
        setAlertMessage('');
        setTopupEnabled(Boolean(d?.topup_email?.enabled));
        setTopupEveryUsd(Number(d?.topup_email?.every_usd || 100));
        setTopupMessage('');
      })
      .catch((e) => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  }, [ownerUserId]);

  useEffect(() => {
    if (!isOpen) return;
    setMember(null);
    setPeriodOffset(0);
    setConsentChecked(false);
    setConsentError(null);
    setRoleError('');
  }, [isOpen, ownerUserId]);

  useEffect(() => {
    if (!isOpen) return;
    loadSummary(periodOffset);
  }, [isOpen, periodOffset, loadSummary]);

  // The plan's credit month (this one, or periodOffset months back), when the
  // company has a plan. Without one (or if the CRM can't be reached) the page
  // stays in dollars. The page waits for it so it never flashes the dollar view.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setCreditLoading(true);
    fetchCompanyCreditBilling(ownerUserId, -periodOffset)
      .then((d) => { if (!cancelled) setCreditData(d?.enabled ? d : null); })
      .catch(() => { if (!cancelled) setCreditData(null); })
      .finally(() => { if (!cancelled) setCreditLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, ownerUserId, periodOffset]);

  const openMember = useCallback((m) => {
    setMember(m);
    setConvOffset(0);
    setConvLoading(true);
    fetchMemberUsageConversations({ userId: m.user_id, offset: 0, limit: PAGE, periodOffset, ownerUserId })
      .then((r) => { setConvs(r.conversations || []); setConvTotal(r.total || 0); setConvHasMore(r.has_more); })
      .catch(() => { setConvs([]); setConvTotal(0); setConvHasMore(false); })
      .finally(() => setConvLoading(false));
  }, [ownerUserId, periodOffset]);

  const pageConvs = useCallback((nextOffset) => {
    if (!member) return;
    setConvLoading(true);
    fetchMemberUsageConversations({ userId: member.user_id, offset: nextOffset, limit: PAGE, periodOffset, ownerUserId })
      .then((r) => { setConvs(r.conversations || []); setConvOffset(nextOffset); setConvHasMore(r.has_more); })
      .catch(() => {})
      .finally(() => setConvLoading(false));
  }, [member, ownerUserId, periodOffset]);

  const saveCap = useCallback(async (clear) => {
    setCapSaving(true);
    try {
      const val = clear ? null : (capInput === '' ? null : Number(capInput));
      const r = await setUsageOverageCap(val);
      setData((d) => ({
        ...d,
        overage_cap_usd: r.overage_cap_usd,
        consent: d?.consent ? {
          ...d.consent,
          status: d.consent.automated_billing_active ? 'active' : 'superseded',
          is_current: Boolean(d.consent.automated_billing_active),
          approved_overage_cap_usd: d.consent.automated_billing_active ? r.overage_cap_usd : d.consent.approved_overage_cap_usd,
          current_overage_cap_usd: r.overage_cap_usd,
        } : d?.consent,
      }));
      if (clear) setCapInput('');
    } catch (e) {
      setConsentError('An active billing account needs a positive usage limit. To stop billing, revoke approval.');
    } finally {
      setCapSaving(false);
    }
  }, [capInput]);

  const changeMemberRole = useCallback(async (userId, role) => {
    setRoleSaving(Number(userId));
    setRoleError('');
    try {
      const result = await updateUsageBillingMemberRole({ userId, role });
      setData((current) => ({
        ...current,
        members: (current?.members || []).map((item) => (
          Number(item.user_id) === Number(userId) ? { ...item, role: result.role } : item
        )),
      }));
    } catch (e) {
      const messages = {
        cannot_change_own_role: 'You cannot change your own role here.',
        cannot_change_admin_role: 'Admin roles are managed separately and cannot be changed here.',
        admin_required: 'Only a company admin can change roles.',
      };
      setRoleError(messages[e.reason] || 'That role could not be changed.');
    } finally {
      setRoleSaving(null);
    }
  }, []);

  const saveAlertPreference = useCallback(async () => {
    const thresholdPct = Number(alertPctInput);
    if (!Number.isFinite(thresholdPct) || thresholdPct < 1 || thresholdPct > 100) {
      setAlertMessage('Enter a percentage from 1 to 100.');
      return;
    }
    setAlertSaving(true);
    setAlertMessage('');
    try {
      const result = await setUsageAlertPreference({
        enabled: alertEnabled,
        thresholdPct,
      });
      setData((current) => ({ ...current, alert_preference: result.alert_preference }));
      setAlertEnabled(Boolean(result.alert_preference?.enabled));
      setAlertPctInput(String(result.alert_preference?.threshold_pct ?? thresholdPct));
      setAlertMessage('Saved');
    } catch (e) {
      setAlertMessage('Could not save this email alert. Please try again.');
    } finally {
      setAlertSaving(false);
    }
  }, [alertEnabled, alertPctInput]);

  const saveTopupEmail = useCallback(async () => {
    setTopupSaving(true);
    setTopupMessage('');
    try {
      const result = await setTopupEmailPreference({ enabled: topupEnabled, everyUsd: topupEveryUsd });
      setData((current) => ({ ...current, topup_email: { ...current?.topup_email, ...result.topup_email } }));
      setTopupEnabled(Boolean(result.topup_email?.enabled));
      setTopupEveryUsd(Number(result.topup_email?.every_usd || topupEveryUsd));
      setTopupMessage('Saved');
    } catch (e) {
      setTopupMessage('Could not save top-up emails. Please try again.');
    } finally {
      setTopupSaving(false);
    }
  }, [topupEnabled, topupEveryUsd]);

  const approveConsent = useCallback(async () => {
    setConsentSaving(true);
    setConsentError(null);
    try {
      const result = await approveUsageBillingConsent();
      setData((d) => ({ ...d, consent: result.consent }));
      setConsentChecked(false);
    } catch (e) {
      const messages = {
        positive_overage_cap_required: 'Set a positive overage cap before approving.',
        active_live_subscription_required: 'A live paid subscription is required before approval.',
        owner_required: 'Only the company owner can approve automated usage billing.',
      };
      setConsentError(messages[e.reason] || 'Approval could not be recorded. Please try again.');
    } finally {
      setConsentSaving(false);
    }
  }, []);

  const revokeConsent = useCallback(async () => {
    setConsentSaving(true);
    setConsentError(null);
    try {
      await revokeUsageBillingConsent();
      setData((d) => ({
        ...d,
        enabled: false,
        consent: { ...d.consent, status: 'revoked', is_current: false, automated_billing_active: false },
      }));
    } catch (e) {
      setConsentError('Approval could not be revoked. Please try again.');
    } finally {
      setConsentSaving(false);
    }
  }, []);

  if (!isOpen) return null;

  const credits = data?.credits || {};
  const usage = data?.usage || {};
  const pct = credits.included_usd > 0
    ? Math.min(100, Math.round(((usage.priced_usd || 0) / credits.included_usd) * 100))
    : 0;
  const overage = Number(data?.overage_usd || 0);
  const cap = data?.overage_cap_usd;
  const capReached = data?.overage_cap_reached;
  const consent = data?.consent || {};
  const canApproveConsent = Boolean(consent.can_approve);
  const hasPositiveCap = Number(cap || 0) > 0;
  const access = data?.access || {};
  const personalOnly = access.scope === 'personal';
  const canManageBilling = Boolean(access.can_manage_billing);
  const canManageRoles = Boolean(access.can_manage_roles);
  const isRealTorchAdminView = Boolean(access.is_realtorch_admin_view);
  // Credits replace dollars for the current month once the company has a plan.
  const creditMode = Boolean(creditData);
  const busy = loading || creditLoading;
  const creditsByMember = Object.fromEntries((creditData?.members || []).map((m) => [Number(m.user_id), m]));
  const memberRows = creditMode
    ? [...(data?.members || [])].sort((a, b) => (
      Number(creditsByMember[Number(b.user_id)]?.credits || 0) - Number(creditsByMember[Number(a.user_id)]?.credits || 0)))
    : (data?.members || []);

  return (
    <div className={embedded ? 'usage-billing-embedded' : 'usage-billing-backdrop'} onClick={embedded ? undefined : onClose}>
      <div className={embedded ? 'usage-billing-embedded-inner' : 'usage-billing-modal'} onClick={(e) => e.stopPropagation()}>
        {!embedded && <div className="usage-billing-header">
          <div className="usage-billing-title">
            <MdInsights size={20} />
            <span>Usage &amp; Billing</span>

          </div>
          <button type="button" className="usage-billing-close" onClick={onClose} aria-label="Close">
            <MdClose size={20} />
          </button>
        </div>}

        <div className="usage-billing-content">
          {busy && <div className="usage-billing-loading">Loading…</div>}
          {error && <div className="usage-billing-error">Couldn&apos;t load usage: {error}</div>}

          {!busy && !error && data?.preview && !creditData && (
            <div className="usage-billing-preview-notice" role="status">
              <strong>Usage awareness</strong>
              <span>Usage billing is not enabled. This page shows your Atlas usage for awareness only.</span>
            </div>
          )}

          {!busy && !error && data?.reason && (
            <div className="usage-billing-empty">
              Billing and usage are not available for this account yet.
            </div>
          )}

          {/* ---- Company summary ---- */}
          {!busy && !error && data && !data.reason && (
            <>
              {creditMode ? (
                <CreditPeriodHeader
                  creditData={creditData}
                  onPrevious={creditData.month?.has_previous ? () => setPeriodOffset((o) => o - 1) : null}
                  onNext={periodOffset < 0 ? () => setPeriodOffset((o) => Math.min(0, o + 1)) : null}
                />
              ) : <div className="usage-billing-period-nav">
                <button type="button" aria-label="Previous period"
                        onClick={() => setPeriodOffset((o) => o - 1)}>
                  <MdChevronLeft size={18} />
                </button>
                <div className="usage-billing-period-center">
                  <div className="usage-billing-period-kicker">
                    {data.period?.is_current ? 'Current billing cycle' : 'Previous billing cycle'}
                  </div>
                  <div className="usage-billing-period-label">{data.period?.label}</div>
                  <div className="usage-billing-period-sub">
                    {data.period?.is_current
                      ? `Renews ${fmtCycleDate(data.period?.billing_resets_on)}`
                      : `Closed ${fmtCycleDate(data.period?.end)}`}
                  </div>
                </div>
                <button type="button" aria-label="Next period"
                        disabled={periodOffset >= 0}
                        onClick={() => setPeriodOffset((o) => Math.min(0, o + 1))}>
                  <MdChevronRight size={18} />
                </button>
              </div>}
              <div className={`usage-billing-access-note${personalOnly ? ' personal' : ''}`}>
                <strong>{isRealTorchAdminView ? 'RealTorch admin view' : personalOnly ? 'Your personal usage' : 'Company usage access'}</strong>
                {isRealTorchAdminView && (
                  <span>{data.enabled
                    ? `You are viewing team-level usage for ${data.company?.name || 'this company'} while switched to this user. Billing controls remain with the company's owners and admins.`
                    : `You are viewing team-level usage for ${data.company?.name || 'this company'} while switched to this user.`}</span>
                )}
                {!isRealTorchAdminView && access.viewer_role === 'admin' && (
                  <span>{data.enabled
                    ? 'Admins can view all team usage, update the overage limit, and change other users to Owner or Member.'
                    : 'Admins can view usage for all team members and change other users to Owner or Member.'}</span>
                )}
                {!isRealTorchAdminView && access.viewer_role === 'owner' && (
                  <span>{data.enabled
                    ? 'Owners can view usage for all users and update the overage limit. Only admins can change roles.'
                    : 'Owners can view usage for all users. Only admins can change roles.'}</span>
                )}
                {!isRealTorchAdminView && personalOnly && (
                  <span>{data.enabled
                    ? 'Members can only view their own usage and cannot control the overage limit.'
                    : 'Members can only view their own usage.'}</span>
                )}
              </div>
              {creditMode && (
                <>
                  <CreditSummary creditData={creditData} personalOnly={personalOnly} />
                  <CreditBreakdown creditData={creditData} personalOnly={personalOnly} />
                </>
              )}
              {!creditMode && !data.enabled && !personalOnly && (
                <div className="usage-billing-personal-total usage-billing-cycle-total">
                  <span>Atlas usage this billing cycle</span>
                  <strong>{fmtUsd(usage.priced_usd)}</strong>
                </div>
              )}
              {!creditMode && data.enabled && !personalOnly && <div className="usage-billing-credits-card">
                <div className="usage-billing-credits-row">
                  <span className="usage-billing-credits-label">Included credits used</span>
                  <span className="usage-billing-credits-nums">
                    {fmtUsd(credits.used_usd)} / {fmtUsd(credits.included_usd)}
                  </span>
                </div>
                <div className="usage-billing-bar">
                  <div className={`usage-billing-bar-fill${overage > 0 ? ' over' : ''}`} style={{ width: `${pct}%` }} />
                </div>
                {overage > 0 ? (
                  <div className="usage-billing-overage">
                    Estimated usage charge this period: <strong>{fmtUsd(data.billable_overage_usd ?? overage)}</strong>
                    {cap != null && (
                      <span className={`usage-billing-cap-tag${capReached ? ' reached' : ''}`}>
                        {capReached ? 'cap reached' : `cap ${fmtUsd(cap)}`}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="usage-billing-remaining">{fmtUsd(credits.remaining_usd)} remaining this period</div>
                )}
              </div>}

              {!creditMode && personalOnly && (
                <div className="usage-billing-personal-total">
                  <span>{data.enabled ? 'Your usage this period' : 'Your Atlas usage this billing cycle'}</span>
                  <strong>{fmtUsd(usage.priced_usd)}</strong>
                </div>
              )}

              {!isRealTorchAdminView && (
                <BillingPortalCard
                  companyId={data.company?.id}
                  canOpen={['owner', 'admin'].includes(access.viewer_role)}
                />
              )}

              {creditMode ? (
                <UsageDailyChart
                  data={creditChartData(creditData, memberRows)}
                  personalOnly={personalOnly}
                  format={fmtCreditTotal}
                  title="Credits by day"
                />
              ) : <UsageDailyChart data={data} personalOnly={personalOnly} />}

              {/* Overage cap control */}
              {canManageBilling && <div className="usage-billing-cap-control">
                <label>Overage limit</label>
                <div className="usage-billing-cap-row">
                  <span className="usage-billing-cap-dollar">$</span>
                  <input
                    type="number" min="0" step="10" placeholder="No cap"
                    value={capInput} onChange={(e) => setCapInput(e.target.value)}
                  />
                  <button type="button" disabled={capSaving} onClick={() => saveCap(false)}>Save</button>
                  {cap != null && (
                    <button type="button" className="usage-billing-cap-clear" disabled={capSaving} onClick={() => saveCap(true)}>Clear</button>
                  )}
                </div>
                <div className="usage-billing-cap-hint">
                  Sets when to ask before continuing. Owners and admins can approve additional billable usage beyond this limit.
                </div>
              </div>}

              {data.alert_preference?.available === true && (
                <div className="usage-billing-alert-control">
                  <div className="usage-billing-alert-title">Usage email alert</div>
                  <div className="usage-billing-alert-row">
                    <label className="usage-billing-alert-toggle">
                      <input
                        type="checkbox"
                        checked={alertEnabled}
                        onChange={(event) => { setAlertEnabled(event.target.checked); setAlertMessage(''); }}
                      />
                      <span>Email me when</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      step="1"
                      aria-label="Usage alert percentage"
                      value={alertPctInput}
                      onChange={(event) => { setAlertPctInput(event.target.value); setAlertMessage(''); }}
                    />
                    <span>% of the overage cap is reached</span>
                    <button type="button" disabled={alertSaving} onClick={saveAlertPreference}>
                      {alertSaving ? 'Saving…' : 'Save alert'}
                    </button>
                  </div>
                  <div className="usage-billing-alert-hint">
                    Sent once per billing period to {data.alert_preference?.email || 'your account email'}.
                    {alertMessage && <span className={alertMessage === 'Saved' ? 'saved' : 'error'}>{alertMessage}</span>}
                  </div>
                </div>
              )}

              {/* Top-ups only exist on credit plans; the CRM sends these emails. */}
              {creditMode && data.topup_email?.available === true && (
                <div className="usage-billing-alert-control usage-billing-topup-control">
                  <div className="usage-billing-alert-title">Top-up emails</div>
                  <div className="usage-billing-alert-row">
                    <label className="usage-billing-alert-toggle">
                      <input
                        type="checkbox"
                        role="switch"
                        checked={topupEnabled}
                        onChange={(event) => { setTopupEnabled(event.target.checked); setTopupMessage(''); }}
                      />
                      <span>Email me when we add a top-up</span>
                    </label>
                    <select
                      aria-label="How often to email about top-ups"
                      value={topupEveryUsd}
                      onChange={(event) => { setTopupEveryUsd(Number(event.target.value)); setTopupMessage(''); }}
                    >
                      {(data.topup_email.options || TOPUP_EMAIL_OPTIONS).map((amount) => (
                        <option key={amount} value={amount}>every {fmtWholeUsd(amount)} of top-ups</option>
                      ))}
                    </select>
                    <button type="button" disabled={topupSaving} onClick={saveTopupEmail}>
                      {topupSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  <div className="usage-billing-alert-hint">
                    Top-ups are added $100 at a time when this month&apos;s credits and carried-over credits run out.
                    {data.topup_email.email ? ` Sent to ${data.topup_email.email}.` : ''}
                    {topupMessage && <span className={topupMessage === 'Saved' ? 'saved' : 'error'}>{topupMessage}</span>}
                  </div>
                </div>
              )}

              {data.period?.is_current && consent.status !== 'not_available' && (
                <div className={`usage-billing-consent-card${consent.is_current ? ' approved' : ''}`}>
                  <div className="usage-billing-consent-title">
                    {consent.automated_billing_active ? 'Automatic usage billing active' : consent.status === 'revoked' ? 'Automatic usage billing paused' : consent.is_current ? 'Overage billing approved' : 'Authorize automated overage billing'}
                  </div>
                  {consent.is_current ? (
                    <>
                      <div className="usage-billing-consent-copy">
                        Usage beyond included credits is billable.
                        {consent.automated_billing_active
                          ? 'Automatic usage billing is active. Continuing past your usage limit adds to your monthly bill.'
                          : 'Automatic usage billing starts only after RealTorch activates your account.'}
                      </div>
                      <div className="usage-billing-consent-meta">
                        Approved {consent.approved_at ? new Date(consent.approved_at).toLocaleDateString() : ''}
                      </div>
                      {canApproveConsent && (
                        <button type="button" className="usage-billing-consent-revoke" disabled={consentSaving} onClick={revokeConsent}>
                          Revoke approval
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="usage-billing-consent-copy">
                        Usage beyond your {creditMode && creditData?.plan?.monthly_credits != null
                          ? `${fmtCredits(creditData.plan.monthly_credits)}`
                          : fmtUsd(credits.included_usd)} included credits may be billed automatically,
                        with a warning at {hasPositiveCap ? fmtUsd(cap) : '—'} per billing period. Explicitly continuing beyond that limit adds billable usage.
                        This approval is recorded now; charging will start only after RealTorch activates it for your account.
                      </div>
                      {!hasPositiveCap ? (
                        <div className="usage-billing-consent-warning">Set a positive overage cap above before approving.</div>
                      ) : canApproveConsent ? (
                        <>
                          <label className="usage-billing-consent-check">
                            <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />
                            <span>I authorize recurring usage charges, including additional usage I approve beyond my limit.</span>
                          </label>
                          <button type="button" className="usage-billing-consent-approve"
                                  disabled={!consentChecked || consentSaving} onClick={approveConsent}>
                            {consentSaving ? 'Recording…' : 'Approve usage billing'}
                          </button>
                        </>
                      ) : (
                        <div className="usage-billing-consent-warning">Only the company owner can approve these billing terms.</div>
                      )}
                    </>
                  )}
                  {consentError && <div className="usage-billing-consent-error">{consentError}</div>}
                </div>
              )}

              {memberRows.length > 0 && (
                <div className="usage-billing-section">
                  <div className="usage-billing-section-title">{personalOnly ? 'Your conversations' : 'By team member'}</div>
                  {roleError && <div className="usage-billing-role-error">{roleError}</div>}
                  <div className="usage-billing-table-scroll">
                  <table className="usage-billing-table">
                    <thead><tr><th>Member</th>{!personalOnly && <th>Role</th>}<th>{creditMode ? 'Credits' : 'Usage'}</th><th /></tr></thead>
                    <tbody>
                      {memberRows.map((m) => {
                        const isOpen = Number(member?.user_id) === Number(m.user_id);
                        return (
                          <React.Fragment key={m.user_id}>
                            <tr
                              className={`usage-billing-conv-row${isOpen ? ' is-open' : ''}`}
                              onClick={() => (isOpen ? setMember(null) : openMember(m))}
                              aria-expanded={isOpen}
                            >
                              <td>
                                <div className="usage-billing-member-name">{m.name || `User ${m.user_id}`}</div>
                                <div className="usage-billing-member-email">{m.email}</div>
                              </td>
                              {!personalOnly && <td onClick={(event) => event.stopPropagation()}>
                                {canManageRoles && m.role !== 'admin' ? (
                                  <select
                                    className="usage-billing-role-select"
                                    value={m.role || 'member'}
                                    disabled={roleSaving === Number(m.user_id)}
                                    aria-label={`Role for ${m.name || m.email || `user ${m.user_id}`}`}
                                    onChange={(event) => changeMemberRole(m.user_id, event.target.value)}
                                  >
                                    <option value="member">Member</option>
                                    <option value="owner">Owner</option>
                                  </select>
                                ) : <span className="usage-billing-role-pill">{m.role || 'member'}</span>}
                              </td>}
                              <td>
                                {creditMode ? (
                                  <>
                                    <div>{fmtCredits(creditsByMember[Number(m.user_id)]?.credits)}</div>
                                    <div className="usage-billing-member-email">
                                      Atlas {fmtCredits(creditsByMember[Number(m.user_id)]?.atlas_credits)} · Data {fmtCredits(creditsByMember[Number(m.user_id)]?.api_credits)}
                                    </div>
                                  </>
                                ) : fmtUsd(m.priced_usd)}
                              </td>
                              <td><MdChevronRight className="usage-billing-member-chevron" size={18} /></td>
                            </tr>
                            {isOpen && (
                              <tr className="usage-billing-member-detail-row">
                                <td colSpan={personalOnly ? 3 : 4}>
                                  <div className="usage-billing-conversations-panel">
                                    <div className="usage-billing-conversations-heading">
                                      <div>
                                        <strong>Conversations</strong>
                                        <span>{convTotal} during {data?.period?.label}</span>
                                      </div>
                                    </div>
                                    {convLoading ? (
                                      <div className="usage-billing-loading">Loading conversations…</div>
                                    ) : convs.length === 0 ? (
                                      <div className="usage-billing-empty">No conversations with usage this period.</div>
                                    ) : (
                                      <div className="usage-billing-conversations-scroll">
                                        <table className="usage-billing-table usage-billing-conversations-table">
                                          <thead><tr><th>Conversation</th><th>{creditMode ? 'Atlas credits' : 'Cost'}</th><th /></tr></thead>
                                          <tbody>
                                            {convs.map((conversation) => (
                                              <tr
                                                key={conversation.conversation_id}
                                                className={onOpenConversation && conversation.can_open ? 'usage-billing-conv-row' : ''}
                                                onClick={() => onOpenConversation && conversation.can_open && onOpenConversation(conversation.conversation_id)}
                                              >
                                                <td>
                                                  <div className="usage-billing-member-name">
                                                    {conversation.title_visible ? conversation.title : 'Private conversation'}
                                                    {conversation.is_company_chat && <span className="usage-billing-company-chat">Company chat</span>}
                                                  </div>
                                                  {conversation.title_visible && <div className="usage-billing-conv-id">{conversation.conversation_id}</div>}
                                                </td>
                                                <td>{creditMode && conversation.credits != null ? fmtCredits(conversation.credits) : fmtUsd(conversation.priced_usd)}</td>
                                                <td>{conversation.can_open && <MdOpenInNew size={15} style={{ opacity: 0.5 }} />}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                    {(convOffset > 0 || convHasMore) && (
                                      <div className="usage-billing-pager">
                                        <button type="button" disabled={convOffset === 0 || convLoading}
                                                onClick={() => pageConvs(Math.max(0, convOffset - PAGE))}>Previous</button>
                                        <span>{convOffset + 1}–{convOffset + convs.length} of {convTotal}</span>
                                        <button type="button" disabled={!convHasMore || convLoading}
                                                onClick={() => pageConvs(convOffset + PAGE)}>Next</button>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              <div className="usage-billing-footnote">
                {creditMode ? creditFootnote(creditData) : data.enabled
                  ? 'Usage is billed in credits included with your subscription; additional usage beyond your included credits is invoiced separately.'
                  : 'These amounts show Atlas usage generated during this billing cycle. Usage billing is not enabled for your company.'}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsageBillingModal;
